import { Injectable, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { BundleEntry, SupplyDelivery, ChargeItemDefinition } from 'fhir/r4';
import { map, switchMap, take, tap } from 'rxjs';
import { backendEndPointToken } from '../app.config';
import { TableFilters, TableFilteringService, TableRowModel } from '../shared/table-filtering.service';
import { StateService } from '../shared/state.service';
import { ChargeItemDefinitionSearchService } from '../shared/charge-item-definition-search.service';
import { UtilityService } from '../shared/utility.service';

export interface SupplyStockRow extends TableRowModel {
    id: string;
    substance: string;
    dispensableUnit?: string;
    totalRemaining?: number;
    totalRemainingUnit?: string;
    packagesRemaining?: number;
    packageName?: string;
    quantityPerPackage?: number;
    quantityPerPackageUnit?: string;
    packagesReceived?: number;
    lastUpdated?: string;
    rawResource: SupplyDelivery;
    lowStock?: boolean;
    lowStockLabel?: string;
    lowStockThreshold?: number;
    lowStockUnit?: string;
    lowStockAlertsEnabled?: boolean;
    chargeItemDefinition?: ChargeItemDefinition;
    priceDisplay?: string;
}

@Injectable({ providedIn: 'root' })
export class SupplyStockSearchService implements OnDestroy {
    private readonly tableFiltering = inject<TableFilteringService<SupplyDelivery, SupplyStockRow>>(TableFilteringService);
    private readonly chargeItemSearch = inject(ChargeItemDefinitionSearchService);
    private readonly backendUrl = inject(backendEndPointToken);
    private readonly stateService = inject(StateService);
    private readonly utilityService = inject(UtilityService);

    private readonly internalDataSource = new MatTableDataSource<SupplyStockRow>();
    private pageSize = 25;

    readonly loading$ = this.tableFiltering.loading$;
    readonly total$ = this.tableFiltering.total$;
    readonly unknownTotal$ = this.tableFiltering.unknownTotal$;
    readonly error$ = this.tableFiltering.error$;

    init(options: { pageSize?: number } = {}): void {
        this.pageSize = options.pageSize ?? this.pageSize;

        // Initialize table filtering with prefetch logic for pricing
        this.chargeItemSearch.prefetch().pipe(
            take(1),
            tap(() => {
                this.tableFiltering.init({
                    resourceType: 'SupplyDelivery',
                    defaultQuery: `/SupplyDelivery?_count=${this.pageSize}&_sort=-occurrence`,
                    dataSource: this.internalDataSource,
                    baseUrl: this.backendUrl,
                    pageSize: this.pageSize,
                    transformEntry: (resource, entry) => this.transformResource(resource, entry)
                });
            })
        ).subscribe();
    }

    get dataSource(): MatTableDataSource<SupplyStockRow> {
        return this.internalDataSource;
    }

    search(filters: TableFilters): void {
         const fhirFilters: TableFilters = {};
        if (filters['itemName']) {
            fhirFilters['_tag:text'] = filters['itemName']; // Map UI search to _tag as per design
        }
        if (filters['status']) {
            fhirFilters['status'] = filters['status'];
        }
        this.tableFiltering.search(fhirFilters);
    }

    changePage(pageIndex: number): void {
        this.tableFiltering.changePage(pageIndex);
    }

    refresh(): void {
        this.tableFiltering.refreshCurrentPage();
    }

    destroy(): void {
        this.tableFiltering.destroy();
        this.internalDataSource.disconnect();
        this.internalDataSource.data = [];
    }

    ngOnDestroy(): void {
        this.destroy();
    }

    private transformResource(resource: SupplyDelivery, entry?: BundleEntry<SupplyDelivery>): SupplyStockRow {
        const id = (resource.id ?? entry?.resource?.id ?? '').toString().trim();
        const fallbackId = id || `SupplyDelivery-${Date.now()}`;

        const substance =
            resource?.suppliedItem?.itemCodeableConcept?.text ||
            resource?.suppliedItem?.itemCodeableConcept?.coding?.[0]?.display ||
            resource?.suppliedItem?.itemCodeableConcept?.coding?.[0]?.code ||
            resource?.suppliedItem?.itemReference?.display ||
            fallbackId;

        const ext = (resource as any)?.extension ?? [];
        const backendUrl = this.backendUrl;

        const totalRemainingExt = ext.find((e: any) => e?.url === backendUrl + '/StructureDefinition/totalRemaining');
        const totalRemaining = Number(totalRemainingExt?.valueQuantity?.value ?? totalRemainingExt?.valueInteger ?? NaN);

        const packagesRemainingExt = ext.find((e: any) => e?.url === backendUrl + '/StructureDefinition/packItemRemaining');
        const packagesRemaining = Number(packagesRemainingExt?.valueInteger ?? packagesRemainingExt?.valueQuantity?.value ?? NaN);

        const pkgDetails = ext.find((e: any) => e?.url === backendUrl + '/StructureDefinition/packageDetails');
        const pkgName = pkgDetails?.extension?.find((e: any) => e?.url === 'name')?.valueString;
        const qtyPerPkg = Number(pkgDetails?.extension?.find((e: any) => e?.url === 'quantityPerPackage')?.valueQuantity?.value ?? NaN);
        const pkgCount = Number(pkgDetails?.extension?.find((e: any) => e?.url === 'packageCount')?.valueQuantity?.value ?? NaN);

        const dispensableUnit =
            totalRemainingExt?.valueQuantity?.unit ||
            (resource?.suppliedItem?.quantity as any)?.unit ||
            undefined;

        const normalizedRemaining = Number.isFinite(totalRemaining) ? totalRemaining : undefined;
        const lowStockConfig = this.utilityService.getSupplyLowStockConfig(resource);
        const lowStock = this.utilityService.isBelowLowStockThreshold(normalizedRemaining, lowStockConfig?.threshold);
        const lowStockLabel = lowStock
            ? this.buildSupplyLowStockLabel(normalizedRemaining, lowStockConfig, totalRemainingExt?.valueQuantity?.unit || dispensableUnit)
            : undefined;

        // Robust pricing lookup: check all codings and fallback to text
        const codings = resource.suppliedItem?.itemCodeableConcept?.coding || [];
        let cid: ChargeItemDefinition | undefined;
        for (const c of codings) {
            if (c.code) {
                cid = this.chargeItemSearch.getDefinitionByCode(c.code);
                if (cid) break;
            }
        }
        if (!cid && resource.suppliedItem?.itemCodeableConcept?.text) {
            cid = this.chargeItemSearch.getDefinitionByCode(resource.suppliedItem.itemCodeableConcept.text);
        }

        let priceDisplay = '—';
        if (cid) {
            priceDisplay = cid.description || cid.title || 'N/A';
        }

        return {
            id: fallbackId,
            resourceType: 'SupplyDelivery',
            display: String(substance).trim() || fallbackId,
            rawResource: resource,
            substance: String(substance).trim() || fallbackId,
            dispensableUnit,
            totalRemaining: normalizedRemaining,
            totalRemainingUnit: totalRemainingExt?.valueQuantity?.unit,
            packagesRemaining: Number.isFinite(packagesRemaining) ? packagesRemaining : undefined,
            packageName: String(pkgName ?? '').trim() || undefined,
            quantityPerPackage: Number.isFinite(qtyPerPkg) ? qtyPerPkg : undefined,
            quantityPerPackageUnit: pkgDetails?.extension?.find((e: any) => e?.url === 'quantityPerPackage')?.valueQuantity?.unit,
            packagesReceived: Number.isFinite(pkgCount) ? pkgCount : undefined,
            lastUpdated: resource.meta?.lastUpdated,
            lowStock,
            lowStockLabel,
            lowStockThreshold: lowStockConfig?.threshold,
            lowStockUnit: lowStockConfig?.unit || totalRemainingExt?.valueQuantity?.unit,
            lowStockAlertsEnabled: lowStockConfig?.alertsEnabled,
            chargeItemDefinition: cid,
            priceDisplay
        };
    }

    private buildSupplyLowStockLabel(
        remaining: number | undefined,
        config: { threshold?: number; unit?: string } | null | undefined,
        fallbackUnit?: string
    ): string {
        const formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
        const remainingText = typeof remaining === 'number'
            ? `${formatter.format(Math.max(0, remaining))}${fallbackUnit ? ` ${fallbackUnit}` : ''}`
            : 'Remaining unknown';
        if (config?.threshold !== undefined) {
            const thresholdUnit = config.unit || fallbackUnit;
            const thresholdText = `${formatter.format(config.threshold)}${thresholdUnit ? ` ${thresholdUnit}` : ''}`;
            return `${remainingText} (threshold ${thresholdText})`;
        }
        return `${remainingText} (low stock)`;
    }
}
